import { Box, Chip, Paper, Stack, Typography } from "@mui/material";
import { ReactNode, useMemo } from "react";
import { IFieldDefinition, IWorkflowBaseRecord } from "../interfaces";

interface IRelationDisplayItem {
  key: string;
  label: string;
  name: string;
  description?: string;
  imageUrl?: string;
}

interface IRelationRecordCardProps {
  record: IWorkflowBaseRecord;
  selected?: boolean;
  onClick?: () => void;
  trailing?: ReactNode;
  fields?: IFieldDefinition<any>[];
}

const getRelationObjectKey = (fieldKey: string): string => {
  if (fieldKey.endsWith("Uid")) {
    return fieldKey.slice(0, -3);
  }

  return fieldKey;
};

const isRecordObject = (value: unknown): value is Record<string, unknown> => {
  return value !== null && typeof value === "object" && !Array.isArray(value);
};

export const RelationRecordCard = (props: IRelationRecordCardProps) => {
  const { record, selected = false, onClick, trailing, fields = [] } = props;

  const relationItems = useMemo<IRelationDisplayItem[]>(() => {
    return fields
      .filter((field) => field.showInRelationCard)
      .map((field) => {
        const relationObjectKey = getRelationObjectKey(String(field.key));
        const rawValue = (record as unknown as Record<string, unknown>)[relationObjectKey];
        const relationObject = isRecordObject(rawValue) ? rawValue : undefined;

        const name = relationObject?.name ?? relationObject?.label ?? relationObject?.title ?? relationObject?.range;

        return {
          key: String(field.key),
          label: field.label,
          name: name ? String(name) : "-",
          description: relationObject?.description ? String(relationObject.description) : undefined,
          imageUrl: relationObject?.imageUrl ? String(relationObject.imageUrl) : undefined,
        };
      })
      .filter((item) => item.name !== "-");
  }, [fields, record]);

  return (
    <Paper
      variant={selected ? "elevation" : "outlined"}
      onClick={onClick}
      sx={{
        p: 2,
        borderRadius: 3,
        borderColor: selected ? "primary.main" : "divider",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <Stack spacing={1.5}>
        {trailing ? (
          <Stack direction="row" justifyContent="flex-end">
            <Box
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              {trailing}
            </Box>
          </Stack>
        ) : null}

        <Stack spacing={1.25}>
          {relationItems.map((item) => (
            <Stack key={item.key} direction="row" spacing={1.5} alignItems="flex-start">
              <Box sx={{ minWidth: 120 }}>
                <Typography variant="caption" color="text.secondary">
                  {item.label}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
                {item.imageUrl ? (
                  <Box
                    component="img"
                    src={item.imageUrl}
                    alt={item.label}
                    sx={{
                      width: 32,
                      height: 32,
                      objectFit: "cover",
                      borderRadius: 1,
                      mt: 0.25,
                    }}
                  />
                ) : null}

                <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {item.name}
                  </Typography>

                  {item.description ? (
                    <Typography variant="caption" color="text.secondary">
                      {item.description}
                    </Typography>
                  ) : null}
                </Stack>
              </Stack>
            </Stack>
          ))}
        </Stack>

        <Chip size="small" label={record.uid} variant="outlined" sx={{ alignSelf: "flex-start" }} />
      </Stack>
    </Paper>
  );
};
